"use client";

import * as React from "react";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { AnimatePresence } from "framer-motion";
import { CopySVG, PlusSVG, ReorderSVG, TikSVG, TrashCanSVG } from "@/components/ui/SVG";
import { Pencil } from "lucide-react";
import LangButton from "@/components/ui/LangButton";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import { ConfirmOverlay } from "@/components/ui/ConfirmOverlay";
import { SortableItem } from "../SortableItem";
import { MediaPickerOverlay } from "@/media/components/MediaPickerOverlay";
import { buildAssetUrl } from "@/media/utils";
import type { Asset } from "@/media/types";
import { Registry } from "@/cms/blocks/registry";
import type { BlockBase } from "@/cms/blocks/registry/types";
import { validateBlocks } from "@/cms/lib/validators";
import { BlockCard } from "./components/BlockCard";
import { DEFAULT_BLOCK_DATA } from "./defaultBlockData";
import { ALL_BLOCK_OPTIONS } from "./blockOptions";
import { CloneContentOverlay, type CloneContentSummary } from "../CloneContentOverlay";
import {
  deepCloneJson,
  uid,
} from "./utils";
import {
  TABLE_SCHEMA_VERSION,
  createTableDoc,
  type TableInitOptions,
} from "@/cms/blocks/table";
import { BlockPickerOverlay } from "./overlays/BlockPickerOverlay";
import { useBlockClipboard } from "./hooks/useBlockClipboard";
import type { BlockEditorProps, ConfirmDialogOptions, ConfirmInvoker, BlockType } from "./types";
import { TableInsertModal } from "./editors/TableEditor";


export function BlockEditor(props: BlockEditorProps) {
  const {
    value,
    onChange,
    locale,
    setLocale,
    isArchived,
    allowedBlockTypes,
    onCloneContentBlocks,
    contentId,
    collapsedBlockIds,
    onToggleCollapseBlock,
  } = props;

  const blocks = React.useMemo(() => value ?? [], [value]);
  const allowedTypes = React.useMemo(
    () => {
      const base = allowedBlockTypes === undefined
        ? ALL_BLOCK_OPTIONS.map((opt) => opt.type)
        : allowedBlockTypes;
      const valid = new Set(ALL_BLOCK_OPTIONS.map((opt) => opt.type));
      return base.filter((t) => valid.has(t));
    },
    [allowedBlockTypes],
  );
  const blockOptions = React.useMemo(
    () => ALL_BLOCK_OPTIONS.filter((opt) => allowedTypes.includes(opt.type)),
    [allowedTypes],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 300, tolerance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 5 },
    }),
  );
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pickerTarget, setPickerTarget] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  const [manageOpen, setManageOpen] = React.useState(false);
  const [reorderActive, setReorderActive] = React.useState(false);
  const [bulkSelected, setBulkSelected] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [bulkCopySuccess, setBulkCopySuccess] = React.useState(false);
  const [blockPickerOpen, setBlockPickerOpen] = React.useState(false);
  const [cloneOverlayOpen, setCloneOverlayOpen] = React.useState(false);
  const [confirmState, setConfirmState] = React.useState<{
    title: string;
    description?: string;
    confirmLabel?: string;
    variant: "danger" | "default";
    onConfirm: () => void;
  } | null>(null);
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
  const [cloning, setCloning] = React.useState(false);
  const [tableInsertOpen, setTableInsertOpen] = React.useState(false);
  const [selectedBlockId, setSelectedBlockId] = React.useState<string | null>(null);
  const prevLengthRef = React.useRef<number>(blocks.length);

  React.useEffect(() => {
    const res = validateBlocks(blocks);
    if (res.ok) {
      setErrors({});
      return;
    }
    const map: Record<string, string[]> = {};
    res.issues.forEach((issue) => {
      if (!issue.blockId) return;
      map[issue.blockId] = map[issue.blockId] || [];
      map[issue.blockId].push(issue.message);
    });
    setErrors(map);
  }, [blocks]);

  React.useEffect(() => {
    if (isArchived) {
      setBlockPickerOpen(false);
      setCloneOverlayOpen(false);
      setManageOpen(false);
      setReorderActive(false);
      setBulkSelected(new Set());
    }
  }, [isArchived]);

  React.useEffect(() => {
    if (!manageOpen) {
      setReorderActive(false);
      setBulkSelected(new Set());
      return;
    }
    setBlockPickerOpen(false);
  }, [manageOpen]);

  React.useEffect(() => {
    if (blocks.length <= prevLengthRef.current) {
      prevLengthRef.current = blocks.length;
      return;
    }
    prevLengthRef.current = blocks.length;
    const last = blocks[blocks.length - 1];
    if (!last) return;
    const targetId = `block-${last.id}`;
    window.setTimeout(() => {
      const node = document.getElementById(targetId);
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  }, [blocks]);

  React.useEffect(() => {
    if (selectedBlockId && !blocks.some((block) => block.id === selectedBlockId)) {
      setSelectedBlockId(null);
    }
  }, [blocks, selectedBlockId]);

  React.useEffect(() => {
    setBulkSelected((prev) => {
      if (prev.size === 0) return prev;
      const ids = new Set(blocks.map((b) => b.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next;
    });
  }, [blocks]);

  const createTableBlock = React.useCallback(
    (options?: TableInitOptions) => {
      if (isArchived) return;
      const def = Registry.get("table");
      if (!def) return;
      const next = [
        ...blocks,
        {
          id: uid(),
          type: "table",
          schemaVersion: def.currentSchemaVersion,
          data: {
            schemaVersion: TABLE_SCHEMA_VERSION,
            doc: createTableDoc(options),
            variant: "default" as const,
          },
        } as BlockBase,
      ];
      onChange(next);
    },
    [blocks, isArchived, onChange],
  );

  function addBlock(type: string) {
    const blockType = type as BlockType;
    if (isArchived) return;
    if (!allowedTypes.includes(blockType)) {
      return;
    }
    if (blockType === "table") {
      setTableInsertOpen(true);
      return;
    }
    const def = Registry.get(blockType);
    if (!def) return;
    const data = DEFAULT_BLOCK_DATA[blockType]?.() ?? {};
    const next = [
      ...blocks,
      {
        id: uid(),
        type: blockType,
        schemaVersion: def.currentSchemaVersion,
        data,
      } as BlockBase,
    ];
    onChange(next);
  }

  function updateBlock(id: string, patch: (data: any) => any) {
    const next = blocks.map((block) =>
      block.id === id ? { ...block, data: patch(block.data) } : block,
    );
    onChange(next);
  }

  function updateBlockMeta(id: string, patch: (meta: any) => any) {
    const next = blocks.map((block) =>
      block.id === id
        ? { ...block, meta: patch(block.meta ?? {}) }
        : block,
    );
    onChange(next);
  }

  function removeBlock(id: string) {
    onChange(blocks.filter((block) => block.id !== id));
  }

  const openConfirmDialog = React.useCallback(
    (config: ConfirmDialogOptions) => {
      setConfirmState({
        title: config.title,
        description: config.description,
        confirmLabel: config.confirmLabel,
        variant: config.variant ?? "default",
        onConfirm: config.onConfirm,
      });
    },
    [],
  );

  const confirmThen = React.useCallback<ConfirmInvoker>(
    (options, onConfirm) =>
      openConfirmDialog({
        ...options,
        onConfirm,
      }),
    [openConfirmDialog],
  );

  const handleConfirmDialog = React.useCallback(() => {
    if (!confirmState) return;
    const action = confirmState.onConfirm;
    setConfirmState(null);
    action?.();
  }, [confirmState]);

  const handleCancelConfirm = React.useCallback(() => {
    setConfirmState(null);
  }, []);

  function requestDelete(id: string) {
    if (isArchived) return;
    const target = blocks.find((block) => block.id === id);
    confirmThen(
      {
        title: "Delete block",
        description: target
          ? `Are you sure you want to delete the "${target.type}" block?`
          : "Are you sure you want to delete this block?",
        confirmLabel: "Delete",
        variant: "danger",
      },
      () => removeBlock(id),
    );
  }

  function onDragEnd(event: any) {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((block) => block.id === active.id);
    const newIndex = blocks.findIndex((block) => block.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(blocks, oldIndex, newIndex));
  }

  function openPicker(blockId: string) {
    setPickerTarget(blockId);
    setPickerOpen(true);
  }

  function applyPicker(asset: Asset) {
    if (!pickerTarget) {
      setPickerOpen(false);
      return;
    }
    const targetBlock = blocks.find((block) => block.id === pickerTarget);
    if (!targetBlock) {
      setPickerOpen(false);
      return;
    }

    const previewUrl = buildAssetUrl(asset);
    const buildImageData = (existing: any) => ({
      ...existing,
      assetId: asset.id,
      url: undefined,
      previewUrl,
      sourceName: asset.originalFilename,
      width: asset.width ?? existing?.width,
      height: asset.height ?? existing?.height,
    });

    updateBlock(pickerTarget, (data: any) => buildImageData(data));
    setPickerOpen(false);
  }

  const canCloneBlocks = typeof onCloneContentBlocks === "function";

  function cloneBlockArray(blocksToClone?: BlockBase[] | null) {
    if (!Array.isArray(blocksToClone)) return [];
    return blocksToClone.map((block) => ({
      ...block,
      id: uid(),
      data: deepCloneJson(block.data),
      meta: block.meta ? deepCloneJson(block.meta) : undefined,
    }));
  }

  function getCloneSummaryTitle(summary: CloneContentSummary) {
    return (
      summary.title_en?.trim() ||
      summary.title_fa?.trim() ||
      summary.slug_en?.trim() ||
      summary.slug_fa?.trim() ||
      summary.id
    );
  }

  async function cloneContentById(
    summary: CloneContentSummary,
    mode: "en" | "fa" | "both",
  ) {
    if (!onCloneContentBlocks) return;
    setCloning(true);
    try {
      const res = await fetch(`/api/cms/content/${summary.id}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data?.message || data?.error || "Failed to load content.",
        );
      }
      const source = data?.content as {
        blocks_en?: BlockBase[];
        blocks_fa?: BlockBase[];
      } | null;
      if (!source) {
        throw new Error("Content not found.");
      }
      const clonedEn = cloneBlockArray(source.blocks_en ?? []);
      const clonedFa = cloneBlockArray(source.blocks_fa ?? []);
      const payload: {
        sourceId: string;
        sourceTitle: string;
        blocks_en?: BlockBase[];
        blocks_fa?: BlockBase[];
      } = {
        sourceId: summary.id,
        sourceTitle: getCloneSummaryTitle(summary),
      };

      if (mode === "en") {
        if (!clonedEn.length) {
          alert("Selected content has no English blocks to clone.");
          return;
        }
        payload.blocks_en = clonedEn;
      } else if (mode === "fa") {
        if (!clonedFa.length) {
          alert("Selected content has no Farsi blocks to clone.");
          return;
        }
        payload.blocks_fa = clonedFa;
      } else {
        if (!clonedEn.length && !clonedFa.length) {
          alert("Selected content has no blocks to clone.");
          return;
        }
        if (clonedEn.length) {
          payload.blocks_en = clonedEn;
        }
        if (clonedFa.length) {
          payload.blocks_fa = clonedFa;
        }
      }

      onCloneContentBlocks(payload);
    } catch (error) {
      console.error("clone content failed", error);
      alert(
        (error as Error)?.message ||
          "Unable to clone the selected content. Please try again.",
      );
    } finally {
      setCloning(false);
    }
  }

  function handleCloneOverlaySelect(
    summary: CloneContentSummary,
    mode: "en" | "fa" | "both",
  ) {
    setCloneOverlayOpen(false);
    void cloneContentById(summary, mode);
  }

  const renderCloneOverlay = React.useCallback(
    () =>
      canCloneBlocks ? (
        <AnimatePresence>
          {cloneOverlayOpen && (
            <CloneContentOverlay
              locale={locale}
              onClose={() => setCloneOverlayOpen(false)}
              onSelect={handleCloneOverlaySelect}
              isCloning={cloning}
            />
          )}
        </AnimatePresence>
      ) : null,
    [canCloneBlocks, cloneOverlayOpen, locale, handleCloneOverlaySelect, cloning],
  );

  const renderConfirmOverlay = React.useCallback(
    () => (
      <AnimatePresence>
        {confirmState && (
          <ConfirmOverlay
            onCancel={handleCancelConfirm}
            onConfirm={handleConfirmDialog}
            title={confirmState.title}
            message={
              confirmState.description ??
              "Are you sure you want to perform this action?"
            }
            confirmLabel={confirmState.confirmLabel ?? "Confirm"}
            variant={confirmState.variant ?? "default"}
          />
        )}
      </AnimatePresence>
    ),
    [confirmState, handleCancelConfirm, handleConfirmDialog],
  );

  
  const { copyBlocksToClipboard, pasteBlockFromClipboard } = useBlockClipboard({
    blocks,
    allowedTypes,
    onChange,
  });

  const bulkCount = bulkSelected.size;
  const bulkList = React.useMemo(
    () => blocks.filter((b) => bulkSelected.has(b.id)),
    [blocks, bulkSelected],
  );

  const toggleBulkChecked = React.useCallback((id: string, checked: boolean) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleBulkCopy = React.useCallback(async () => {
    if (bulkCount === 0) return;
    const ok = await copyBlocksToClipboard(bulkList.map((b) => ({ ...b })));
    if (ok) {
      setBulkCopySuccess(true);
      window.setTimeout(() => setBulkCopySuccess(false), 1500);
    }
  }, [bulkCount, bulkList, copyBlocksToClipboard]);

  const requestBulkDelete = React.useCallback(() => {
    if (bulkCount === 0) return;
    confirmThen(
      {
        title: `Delete ${bulkCount} block${bulkCount === 1 ? "" : "s"}?`,
        description: "This action cannot be undone.",
        confirmLabel: "Delete",
        variant: "danger",
      },
      () => {
        const next = blocks.filter((b) => !bulkSelected.has(b.id));
        onChange(next);
        setBulkSelected(new Set());
      },
    );
  }, [blocks, bulkCount, bulkSelected, confirmThen, onChange]);

  const handleTableInsert = React.useCallback(
    (options: Required<TableInitOptions>) => {
      createTableBlock(options);
      setTableInsertOpen(false);
    },
    [createTableBlock],
  );

  const heroImageInUse = React.useMemo(
    () =>
      blocks.some(
        (block) =>
          block.type === "image" &&
          ((block.data as any)?.variant ?? "default") === "hero",
      ),
    [blocks],
  );

  const shortDescriptionInUse = React.useMemo(
    () =>
      blocks.some(
        (block) =>
          block.type === "paragraph" &&
          ((block.data as any)?.variant ?? "default") === "shortDescription",
      ),
    [blocks],
  );

  return (
    <div className="flex flex-col gap-3 px-3 pt-2 pb-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`bg-pinkCustom rounded-md px-2 py-1 text-white transition ${
              isArchived || reorderActive || manageOpen || blockOptions.length === 0
                ? "cursor-not-allowed opacity-50"
                : ""
            }`}
            onClick={() => {
              if (isArchived || reorderActive || manageOpen || blockOptions.length === 0) return;
              setBlockPickerOpen(true);
            }}
            disabled={isArchived || reorderActive || manageOpen || blockOptions.length === 0}
          >
            <PlusSVG className="w-5" svgColor="var(--color-white)" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (isArchived) return;
              setManageOpen((prev) => !prev);
            }}
            disabled={isArchived}
            className={`mx-1 rounded px-2 py-1 transition ${
              manageOpen ? "bg-red-600 text-white" : "text-pinkCustom border-pinkCustom"
            } ${
              isArchived
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-pinkCustom/10"
            }`}
          >
            <Pencil
              className={`h-[22px] w-[22px] ${
                manageOpen ? "text-white" : "text-pinkCustom"
              }`}
            />
          </button>

          {manageOpen ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`rounded-md border px-2 py-1 text-xs font-semibold transition ${
                  reorderActive
                    ? "border-pinkCustom bg-pinkCustom/10 text-pinkCustom"
                    : "border-gray-200 text-slate-700 hover:bg-slate-50"
                }`}
                onClick={() => setReorderActive((prev) => !prev)}
                disabled={blocks.length < 2}
                title="Reorder blocks"
              >
                <ReorderSVG className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                onClick={() => void handleBulkCopy()}
                disabled={bulkCount === 0}
                title="Copy selected"
              >
                {bulkCopySuccess ? (
                  <TikSVG className="h-4 w-4" svgColor="pinkCustom" />
                ) : (
                  <CopySVG className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                className="rounded-md border border-gray-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                onClick={requestBulkDelete}
                disabled={bulkCount === 0}
                title="Delete selected"
              >
                <TrashCanSVG className="h-4 w-4" svgColor="var(--color-slate-700)" accentColor="var(--color-slate-700)" />
              </button>
            </div>
          ) : null}
        </div>
        <LangButton
          bgColor="grayCustom"
          currentLang={locale}
          setCurrentLang={setLocale}
          disabled={reorderActive}
        />
      </div>

      {cloning && (
        <div className="border-pinkCustom/40 bg-pinkCustom/5 text-pinkCustom flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <DotPulseLdr size={24} color="var(--color-pinkCustom)" />
          <span>Cloning content…</span>
        </div>
      )}

      {isArchived && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          This content is archived. Unarchive before making further changes.
        </div>
      )}

      {reorderActive ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={({ active }) =>
            setActiveDragId(active?.id.toString() ?? null)
          }
          onDragCancel={() => setActiveDragId(null)}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={blocks.map((block) => block.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {blocks.map((block, index) => (
                <SortableItem
                  id={block.id}
                  key={block.id}
                  wrapperId={`block-${block.id}`}
                  dataBlockId={block.id}
                  wrapperStyle={({ isDragging }) => ({
                    opacity: isDragging ? 1 : 1,
                    filter: isDragging ? undefined : "blur(0.5px)",
                  })}
                >
                  {({ attributes, listeners }) => (
                    <BlockCard
                      block={block}
                      locale={locale}
                      indexNumber={index + 1}
                      collapsed={Boolean(collapsedBlockIds?.has(block.id))}
                      onToggleCollapse={() => onToggleCollapseBlock?.(block.id)}
                      selection={{
                        showCheckbox: manageOpen,
                        disableCheckbox: true,
                        checked: bulkSelected.has(block.id),
                        isSelected: selectedBlockId === block.id,
                        highlightBorder: manageOpen,
                      }}
                      errorMessages={errors[block.id]}
                      onChange={(patch) => updateBlock(block.id, patch)}
                      onChangeMeta={(patch) => updateBlockMeta(block.id, patch)}
                      actions={{
                        onRemove: () => requestDelete(block.id),
                        onCopy: async () => false,
                        onPickImage: () => openPicker(block.id),
                        hideCopyAction: true,
                        hideDeleteControl: true,
                      }}
                      metaFlags={{
                        heroImageInUse,
                        shortDescriptionInUse,
                        editMode: true,
                      }}
                      drag={{
                        attributes,
                        listeners,
                        isDragging: activeDragId === block.id,
                        handleOnly: true,
                      }}
                      onRequestConfirm={confirmThen}
                      showReorderIcon={manageOpen}
                    />
                  )}
                </SortableItem>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {blocks.map((block, index) => (
            <div key={block.id} id={`block-${block.id}`} data-block-id={block.id}>
              <BlockCard
                block={block}
                locale={locale}
                indexNumber={index + 1}
                collapsed={Boolean(collapsedBlockIds?.has(block.id))}
                onToggleCollapse={() => onToggleCollapseBlock?.(block.id)}
                selection={{
                  showCheckbox: manageOpen,
                  disableCheckbox: false,
                  checked: bulkSelected.has(block.id),
                  onCheckChange: (next) => toggleBulkChecked(block.id, next),
                  isSelected: selectedBlockId === block.id,
                  onSelect: () => {
                    if (manageOpen) return;
                    setSelectedBlockId((prev) =>
                      prev === block.id ? null : block.id,
                    );
                  },
                  highlightBorder: manageOpen,
                }}
                errorMessages={errors[block.id]}
                onChange={(patch) => updateBlock(block.id, patch)}
                onChangeMeta={(patch) => updateBlockMeta(block.id, patch)}
                actions={{
                  onRemove: () => requestDelete(block.id),
                  onCopy: async () => false,
                  onPickImage: () => openPicker(block.id),
                  hideCopyAction: true,
                  hideDeleteControl: manageOpen,
                }}
                metaFlags={{
                  heroImageInUse,
                  shortDescriptionInUse,
                  editMode: false,
                }}
                drag={{ attributes: {}, listeners: {}, isDragging: false, handleOnly: true }}
                onRequestConfirm={confirmThen}
                showReorderIcon={false}
              />
            </div>
          ))}
        </div>
      )}

      <MediaPickerOverlay
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(asset) => applyPicker(asset)}
        accept={["image/*"]}
        usage={
          contentId
            ? { context: "cms-image-block", contentId }
            : { context: "cms-image-block" }
        }
        title="Select image"
        description="Choose an image from the media library."
      />

      <TableInsertModal
        open={tableInsertOpen}
        onClose={() => setTableInsertOpen(false)}
        onSubmit={handleTableInsert}
      />

      <BlockPickerOverlay
        open={blockPickerOpen}
        onClose={() => setBlockPickerOpen(false)}
        blockOptions={blockOptions}
        onAddBlock={addBlock}
        canCloneBlocks={canCloneBlocks}
        cloning={cloning}
        onOpenClone={() => setCloneOverlayOpen(true)}
        onPasteBlock={() => void pasteBlockFromClipboard("button")}
      />

      {renderCloneOverlay()}
      {renderConfirmOverlay()}

      <button
        type="button"
        className="fixed bottom-6 right-6 z-40 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-lg ring-1 ring-gray-200 transition hover:bg-gray-50"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        Back to top
      </button>
    </div>
  );
}
